import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  getMe,
  loginUser,
  logoutAllSessions,
  logoutUser,
} from "../api/auth";
import { getApiError } from "../api/errors";
import {
  clearTenantContext,
  clearTokens,
  getAccessToken,
  getStoreId,
  getWorkspaceId,
  hasSessionMarker,
  saveTenantContext,
  saveTokens,
} from "../api/session";
import { getWorkspaces } from "../api/workspaces";
import { getLegalConsentStatus } from "../api/legal";
import { useConnectivity } from "./ConnectivityContext";
import { clearOfflinePosSession, loadOfflineAuthSnapshot } from "../offline/session";

const AuthContext = createContext(null);

const MANAGEMENT_ROLES = new Set(["owner", "admin"]);
const EDITOR_ROLES = new Set(["owner", "admin", "staff"]);

function isRecoverableConnectivityError(error) {
  const status = Number(error?.response?.status || 0);
  return !error?.response || status >= 500;
}

function resolveSelection(workspaces, preferredWorkspaceId, preferredStoreId) {
  if (!workspaces.length) {
    return { workspace: null, store: null };
  }

  const workspace =
    workspaces.find((item) => item.public_id === preferredWorkspaceId) ||
    workspaces[0];

  const store =
    workspace.stores?.find((item) => item.public_id === preferredStoreId) ||
    workspace.stores?.[0] ||
    null;

  return { workspace, store };
}

export function AuthProvider({ children }) {
  const { state: connectivityState } = useConnectivity();
  const [user, setUser] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [authError, setAuthError] = useState("");
  const [legalStatus, setLegalStatus] = useState(null);
  const [legalStatusLoading, setLegalStatusLoading] = useState(false);
  const [offlineAuthActive, setOfflineAuthActive] = useState(false);
  const legalRequestSequence = useRef(0);

  const clearLocalSession = useCallback((message = "") => {
    clearTokens();
    clearTenantContext();
    setUser(null);
    setWorkspaces([]);
    setSelectedWorkspace(null);
    setSelectedStore(null);
    setLegalStatus(null);
    setLegalStatusLoading(false);
    setOfflineAuthActive(false);
    legalRequestSequence.current += 1;
    if (message) localStorage.setItem("auth_error", message);
  }, []);

  const applyWorkspaceOptions = useCallback((options, preferredWorkspaceId, preferredStoreId) => {
    const normalized = Array.isArray(options) ? options : [];
    const selection = resolveSelection(
      normalized,
      preferredWorkspaceId,
      preferredStoreId,
    );

    setWorkspaces(normalized);
    setSelectedWorkspace(selection.workspace);
    setSelectedStore(selection.store);
    saveTenantContext(
      selection.workspace?.public_id || null,
      selection.store?.public_id || null,
    );

    return selection;
  }, []);

  const restoreOfflineAuthenticatedState = useCallback(async () => {
    const workspaceId = getWorkspaceId();
    const storeId = getStoreId();
    if (!workspaceId || !storeId) return false;

    const snapshot = await loadOfflineAuthSnapshot(workspaceId, storeId);
    if (!snapshot) return false;

    const identity = snapshot.identity;
    const workspace = {
      ...(identity.workspace || {}),
      role: identity.role,
      effective_status: identity.workspace?.effective_status || "active",
      stores: [identity.store],
    };
    setUser(identity.user);
    setWorkspaces([workspace]);
    setSelectedWorkspace(workspace);
    setSelectedStore(identity.store);
    setLegalStatus({
      required: false,
      offline_verified: true,
      terms_version: identity.terms_version,
      privacy_version: identity.privacy_version,
    });
    setLegalStatusLoading(false);
    setOfflineAuthActive(true);
    setAuthError("");
    return true;
  }, []);

  const refreshLegalStatus = useCallback(async (workspacePublicId) => {
    if (!workspacePublicId) {
      setLegalStatus(null);
      setLegalStatusLoading(false);
      return null;
    }

    const requestSequence = legalRequestSequence.current + 1;
    legalRequestSequence.current = requestSequence;
    setLegalStatus(null);
    setLegalStatusLoading(true);
    try {
      const response = await getLegalConsentStatus(workspacePublicId);
      if (legalRequestSequence.current === requestSequence) {
        setLegalStatus(response.data);
      }
      return response.data;
    } catch (error) {
      if (legalRequestSequence.current === requestSequence) {
        setLegalStatus({ required: true, verification_error: true });
      }
      throw error;
    } finally {
      if (legalRequestSequence.current === requestSequence) {
        setLegalStatusLoading(false);
      }
    }
  }, []);

  const loadAuthenticatedState = useCallback(async () => {
    const [userResponse, workspaceResponse] = await Promise.all([
      getMe(),
      getWorkspaces(),
    ]);

    setUser(userResponse.data);
    setOfflineAuthActive(false);
    setAuthError("");
    const selection = applyWorkspaceOptions(
      workspaceResponse.data,
      getWorkspaceId(),
      getStoreId(),
    );
    const resolvedLegalStatus = selection.workspace
      ? await refreshLegalStatus(selection.workspace.public_id)
      : null;

    return { user: userResponse.data, selection, legalStatus: resolvedLegalStatus };
  }, [applyWorkspaceOptions, refreshLegalStatus]);

  useEffect(() => {
    if (!initializing) return undefined;
    if (connectivityState === "checking" || connectivityState === "reconnecting") {
      return undefined;
    }

    let active = true;

    async function bootstrap() {
      const shouldRestore = Boolean(getAccessToken()) || hasSessionMarker();
      if (!shouldRestore) {
        if (active) setInitializing(false);
        return;
      }

      if (connectivityState !== "online") {
        const restored = await restoreOfflineAuthenticatedState().catch(() => false);
        if (!restored && active) {
          setAuthError("ABOS is unreachable. Offline POS is available only on an enrolled device with a valid cached grant.");
        }
        if (active) setInitializing(false);
        return;
      }

      try {
        await loadAuthenticatedState();
      } catch (error) {
        if (isRecoverableConnectivityError(error)) {
          const restored = await restoreOfflineAuthenticatedState().catch(() => false);
          if (!restored && active) {
            setAuthError("ABOS is unreachable. Offline POS is available only on an enrolled device with a valid cached grant.");
          }
        } else {
          await clearOfflinePosSession(getWorkspaceId(), getStoreId()).catch(() => {});
          clearLocalSession();
        }
      } finally {
        if (active) setInitializing(false);
      }
    }

    bootstrap();
    return () => {
      active = false;
    };
  }, [
    clearLocalSession,
    connectivityState,
    initializing,
    loadAuthenticatedState,
    restoreOfflineAuthenticatedState,
  ]);

  useEffect(() => {
    const handleExpired = () => {
      clearOfflinePosSession(getWorkspaceId(), getStoreId()).catch(() => {});
      clearLocalSession("Your session expired. Please sign in again.");
    };

    window.addEventListener("auth:expired", handleExpired);
    return () => window.removeEventListener("auth:expired", handleExpired);
  }, [clearLocalSession]);

  useEffect(() => {
    const handleLegalRequired = (event) => {
      setLegalStatus((current) => ({
        ...(current || {}),
        required: true,
        ...(event?.detail || {}),
      }));
      setLegalStatusLoading(false);
    };

    window.addEventListener("legal:required", handleLegalRequired);
    return () => window.removeEventListener("legal:required", handleLegalRequired);
  }, []);

  const login = useCallback(
    async (identifier, password) => {
      setAuthError("");
      try {
        const response = await loginUser({ identifier, password });
        saveTokens(response.data);
        return await loadAuthenticatedState();
      } catch (error) {
        clearLocalSession();
        const message = getApiError(error, "Could not sign in.");
        setAuthError(message);
        throw error;
      }
    },
    [clearLocalSession, loadAuthenticatedState],
  );

  const logout = useCallback(async () => {
    try {
      if (getAccessToken()) await logoutUser();
    } catch (error) {
      // Local logout must still succeed if the API is offline or the token expired.
    } finally {
      await clearOfflinePosSession(getWorkspaceId(), getStoreId()).catch(() => {});
      clearLocalSession();
    }
  }, [clearLocalSession]);

  const logoutAll = useCallback(async () => {
    await logoutAllSessions();
    await clearOfflinePosSession(getWorkspaceId(), getStoreId()).catch(() => {});
    clearLocalSession();
  }, [clearLocalSession]);

  const switchWorkspace = useCallback(
    (workspacePublicId) => {
      const workspace = workspaces.find(
        (item) => item.public_id === workspacePublicId,
      );
      if (!workspace) return;

      const store = workspace.stores?.[0] || null;
      setSelectedWorkspace(workspace);
      setSelectedStore(store);
      saveTenantContext(workspace.public_id, store?.public_id || null);
      refreshLegalStatus(workspace.public_id).catch(() => {
        // Protected routes remain blocked while the legal status cannot be verified.
      });
    },
    [refreshLegalStatus, workspaces],
  );

  const switchStore = useCallback(
    (storePublicId) => {
      const store = selectedWorkspace?.stores?.find(
        (item) => item.public_id === storePublicId,
      );
      if (!store) return;

      setSelectedStore(store);
      saveTenantContext(selectedWorkspace.public_id, store.public_id);
    },
    [selectedWorkspace],
  );

  const refreshWorkspaces = useCallback(async () => {
    const response = await getWorkspaces();
    const selection = applyWorkspaceOptions(
      response.data,
      selectedWorkspace?.public_id || getWorkspaceId(),
      selectedStore?.public_id || getStoreId(),
    );
    if (selection.workspace) {
      await refreshLegalStatus(selection.workspace.public_id);
    } else {
      setLegalStatus(null);
      setLegalStatusLoading(false);
    }
    return selection;
  }, [applyWorkspaceOptions, refreshLegalStatus, selectedStore, selectedWorkspace]);

  useEffect(() => {
    if (!offlineAuthActive || connectivityState !== "online") return undefined;
    let cancelled = false;

    loadAuthenticatedState()
      .catch(async (error) => {
        if (cancelled) return;
        if (!isRecoverableConnectivityError(error)) {
          await clearOfflinePosSession(getWorkspaceId(), getStoreId()).catch(() => {});
          clearLocalSession("Your online session must be restored before continuing.");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [clearLocalSession, connectivityState, loadAuthenticatedState, offlineAuthActive]);

  const role = selectedWorkspace?.role || null;
  const platformRole = user?.platform_role || null;
  const isSystemAccount = user?.entitlement_type === "system";
  const workspaceStatus = selectedWorkspace?.effective_status || selectedWorkspace?.status || null;
  const workspaceOperational = workspaceStatus === "active";
  const legalAcceptanceRequired = Boolean(legalStatus?.required);
  const value = useMemo(
    () => ({
      user,
      workspaces,
      selectedWorkspace,
      selectedStore,
      role,
      platformRole,
      initializing,
      authError,
      legalStatus,
      legalStatusLoading,
      legalAcceptanceRequired,
      offlineAuthActive,
      connectivityState,
      isAuthenticated: Boolean(user),
      isPlatformAdmin: Boolean(platformRole),
      isSystemAccount,
      contextReady: Boolean(selectedWorkspace && selectedStore),
      workspaceStatus,
      workspaceOperational,
      canManage: workspaceOperational && MANAGEMENT_ROLES.has(role),
      canEdit: workspaceOperational && EDITOR_ROLES.has(role),
      login,
      logout,
      logoutAll,
      switchWorkspace,
      switchStore,
      refreshWorkspaces,
      refreshLegalStatus,
      clearAuthError: () => setAuthError(""),
    }),
    [
      authError,
      connectivityState,
      initializing,
      offlineAuthActive,
      legalAcceptanceRequired,
      legalStatus,
      legalStatusLoading,
      login,
      logout,
      logoutAll,
      refreshLegalStatus,
      refreshWorkspaces,
      role,
      platformRole,
      isSystemAccount,
      workspaceStatus,
      workspaceOperational,
      selectedStore,
      selectedWorkspace,
      switchStore,
      switchWorkspace,
      user,
      workspaces,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
